import { useMutation, useSuspenseQuery } from "@apollo/client";
import { getRouteApi } from "@tanstack/react-router";
import { format } from "date-fns";
import { Pen } from "lucide-react";
import { Suspense, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { BookingStatusBadge } from "../BookingStatusBadge";
import { BookingDocuments } from "@/bookings/BookingDocuments";
import { BookingForm, type BookingFormProps } from "@/bookings/BookingForm";
import { type BookingFormSchema } from "@/bookings/BookingForm/validation";
import { BookingMetadata, BookingMetadataError, BookingMetadataSkeleton } from "@/bookings/BookingMetadata";
import { BookingSkeleton } from "@/bookings/BookingSkeleton";
import { DocumentTileError, DocumentTileSkeleton } from "@/components/DocumentTile";
import { ErrorState } from "@/components/ErrorState";
import { InfoField } from "@/components/InfoField";
import { Heading1 } from "@/components/Typography";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardTitle } from "@/components/ui/card";
import { graphql, type ResultOf } from "@/graphql";
import { useToast } from "@/hooks/use-toast";
import { removeNothings } from "@/lib/utils/arrays";
import { nullable } from "@/lib/utils/common";
import { toDisplayDate, toJsDate } from "@/lib/utils/dates";
import { extractErrorMessages, getErrorMessage } from "@/lib/utils/errors";
import { formatAddressWithCompanyName, formatEmptyableValue, formatName } from "@/lib/utils/formatting";

const routeApi = getRouteApi("/(authed)/_layout/bookings/$bookingId");

export type Booking = ResultOf<typeof supplyChainBooking>["supplyChainBooking"];

export default function Booking({ bookingName }: { bookingName: string }) {
  const { bookingId } = routeApi.useParams();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <BookingHeader bookingName={bookingName} onEdit={() => setIsOpen(true)} />

      <Card className="m-4 p-4 bg-muted-light">
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <ErrorBoundary FallbackComponent={({ error }) => <ErrorState message={getErrorMessage(error)} />}>
            <Suspense fallback={<BookingSkeleton numItems={9} aria-label="Loading booking details" />}>
              <BookingDetails bookingId={bookingId} setIsOpen={setIsOpen} isOpen={isOpen} />
            </Suspense>
          </ErrorBoundary>
        </div>
      </Card>

      <hr className="border-border" />
      <div className="bg-muted flex flex-col h-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4 items-start">
          <ErrorBoundary FallbackComponent={DocumentTileError}>
            <Suspense fallback={<DocumentTileSkeleton />}>
              <BookingDocuments bookingId={bookingId} />
            </Suspense>
          </ErrorBoundary>

          <ErrorBoundary FallbackComponent={BookingMetadataError}>
            <Suspense fallback={<BookingMetadataSkeleton />}>
              <BookingMetadata bookingId={bookingId} />
            </Suspense>
          </ErrorBoundary>
        </div>
      </div>
    </>
  );
}

function BookingHeader({ bookingName, onEdit }: { bookingName: string; onEdit: () => void }) {
  return (
    <header data-testid="booking-header" className="flex justify-between h-16">
      <Heading1 className="truncate px-4 py-2" title={bookingName}>
        {bookingName}
      </Heading1>
      <Button className="m-4" variant="outline" onClick={onEdit} aria-label="Edit Booking">
        <Pen className="h-4 w-4" />
        Edit
      </Button>
    </header>
  );
}

function BookingDetails({
  bookingId,
  isOpen,
  setIsOpen,
}: {
  bookingId: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}) {
  const { data } = useSuspenseQuery(supplyChainBooking, {
    variables: { id: bookingId },
  });
  const [updateSupplyChainBooking] = useMutation(updateSupplyChainBookingMutation);
  const { toast } = useToast();

  const onSubmit = async (values: BookingFormSchema) => {
    try {
      const result = await updateSupplyChainBooking({
        variables: {
          input: {
            booking: bookingId,
            // Basic booking fields
            name: values.name,
            status: values.status,
            plannedShippingDate: format(values.plannedShipmentDate, "yyyy-MM-dd"),
            numberOfHead: values.numberOfHead,
            note: nullable(values.note),
            destination: nullable(values.destination),
            source: nullable(values.source),
            accountManager: nullable(values.businessAccountManagerId),
            // Metadata fields
            accountNumber: nullable(values.ACCOUNT_NUMBER),
            accreditations: values.ACCREDITATIONS?.length ? values.ACCREDITATIONS : [],
            brandNote: nullable(values.BRAND_NOTE),
            breedNote: nullable(values.BREED_NOTE),
            broker: nullable(values.BROKER),
            certificateNumber: nullable(values.CERTIFICATE_NUMBER),
            confirmed: values.CONFIRMED,
            dateDelivered: values.DATE_DELIVERED ? format(values.DATE_DELIVERED, "yyyy-MM-dd") : null,
            dateOfSale: values.DATE_OF_SALE ? format(values.DATE_OF_SALE, "yyyy-MM-dd") : null,
            estimatedChoicePercent: !!values.ESTIMATED_CHOICE_PERCENT ? Number(values.ESTIMATED_CHOICE_PERCENT) : null,
            estimatedWeightNote: nullable(values.ESTIMATED_WEIGHT_NOTE),
            freightCost: nullable(values.FREIGHT_COST),
            grid: nullable(values.GRID),
            lotNumber: nullable(values.LOT_NUMBER),
            penNumber: nullable(values.PEN_NUMBER),
            price: nullable(values.PRICE),
            program: nullable(values.PROGRAM),
            purchasePricePerCwt: nullable(values.PURCHASE_PRICE_PER_CWT),
            riskManagement: values.RISK_MANAGEMENT,
            serviceProvided: nullable(values.SERVICE_PROVIDED),
            sexClass: nullable(values.SEX_CLASS),
            shippedWeight: !!values.SHIPPED_WEIGHT ? Number(values.SHIPPED_WEIGHT) : null,
            shrinkPercent: !!values.SHRINK_PERCENT ? Number(values.SHRINK_PERCENT) : null,
            slide: values.SLIDE ? Number(values.SLIDE) : null,
            sourceNote: nullable(values.SOURCE_NOTE),
            truckingCompany: nullable(values.TRUCKING_COMPANY),
            veterinarian: nullable(values.VETERINARIAN),
            baseWeight: !!values.BASE_WEIGHT ? Number(values.BASE_WEIGHT) : null,
            labels: values.LABELS?.length ? values.LABELS : [],
            dateOfVisit: values.DATE_OF_VISIT ? format(values.DATE_OF_VISIT, "yyyy-MM-dd") : null,
            condition: nullable(values.CONDITION),
            conditions: values.CONDITIONS?.length ? values.CONDITIONS : [],
            ownershipType: nullable(values.OWNERSHIP_TYPE),
            projectedOutDate: values.PROJECTED_OUT_DATE ? format(values.PROJECTED_OUT_DATE, "yyyy-MM-dd") : null,
          },
        },
      });

      const errors = result.errors || [];

      if (errors.length > 0) {
        toast({
          title: `Error updating ${values.name}`,
          description: extractErrorMessages(errors),
          variant: "destructive",
        });

        return;
      }

      setIsOpen(false);

      toast({
        title: "Success",
        description: `Booking ${values.name} updated successfully`,
        variant: "default",
      });
    } catch (error) {
      toast({
        title: `Error updating ${values.name}`,
        description: getErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const onCancel = () => {
    setIsOpen(false);
  };

  const booking = data.supplyChainBooking;
  const bookingInfo = [
    {
      label: "Booking Name",
      value: formatEmptyableValue(booking?.name),
      fullWidth: false,
    },
    {
      label: "Status",
      value: booking?.status ? <BookingStatusBadge status={booking.status} /> : "\u2014",
      fullWidth: false,
    },
    {
      label: "Number of Head",
      value: formatEmptyableValue(booking?.numberOfHead?.toString() || ""),
      fullWidth: false,
    },
    {
      label: "Date Created",
      value: toDisplayDate(booking?.createdAt || undefined, "MM/dd/yyyy"),
      fullWidth: false,
    },
    {
      label: "Planned Shipping Date",
      value: toDisplayDate(booking?.plannedShippingDate || undefined, "MM/dd/yyyy"),
      fullWidth: false,
    },
    {
      label: "Booking Manager",
      value: formatName({
        firstName: booking?.accountManager?.user.firstName,
        lastName: booking?.accountManager?.user.lastName,
      }),
      fullWidth: false,
    },
    {
      label: "Note",
      value: booking?.note ? <div className="whitespace-pre-wrap break-words">{booking.note}</div> : "—",
      fullWidth: Boolean(booking?.note),
    },
    {
      label: "Source",
      value: formatAddressWithCompanyName(booking?.source?.address, booking?.source?.supplyChainPartner?.businessName),
      fullWidth: Boolean(booking?.source?.address),
    },
    {
      label: "Destination",
      value: formatAddressWithCompanyName(
        booking?.destination?.address,
        booking?.destination?.supplyChainPartner?.businessName,
      ),
      fullWidth: Boolean(booking?.destination?.address),
    },
  ] as const;

  return (
    <>
      <article className="contents" data-testid="booking-details">
        {bookingInfo.map(({ label, value, fullWidth }) => (
          <InfoField key={label} fullWidth={fullWidth}>
            <CardDescription className="text-foreground/80">{label}</CardDescription>
            <CardTitle
              className={typeof value === "string" ? "truncate leading-relaxed" : "leading-relaxed"}
              title={typeof value === "string" ? value : undefined}
            >
              {value}
            </CardTitle>
          </InfoField>
        ))}
      </article>

      <AlertDialog
        open={isOpen}
        onOpenChange={(open) => {
          if (open) {
          }
          setIsOpen(open);
        }}
      >
        <AlertDialogContent className="max-w-[800px] max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Edit Booking</AlertDialogTitle>
            <AlertDialogDescription className="sr-only">Edit Booking Form</AlertDialogDescription>
          </AlertDialogHeader>
          <BookingForm onSubmit={onSubmit} onCancel={onCancel} initialData={mapBookingToFormData(booking)} />
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

const mapBookingToFormData = (booking: Booking): BookingFormProps["initialData"] => {
  if (!booking) return undefined;

  return {
    // Basic booking fields
    name: booking.name || "",
    status: booking.status || "DRAFT",
    plannedShipmentDate: booking.plannedShippingDate ? toJsDate(booking.plannedShippingDate) : new Date(),
    numberOfHead: booking.numberOfHead || undefined,
    note: booking.note || "",
    destination: booking.destination?.id || null,
    businessAccountManagerId: booking.accountManager?.user?.id?.toString() || null,
    source: booking.source?.id || null,

    // Metadata fields
    ACCOUNT_NUMBER: booking.accountNumber || "",
    ACCREDITATIONS: booking.accreditations?.map((acc) => acc.id) || [],
    BRAND_NOTE: booking.brandNote || "",
    BREED_NOTE: booking.breedNote || "",
    BROKER: booking.broker?.id || undefined,
    CERTIFICATE_NUMBER: booking.certificateNumber || "",
    CONFIRMED: booking.confirmed || false,
    DATE_DELIVERED: booking.dateDelivered ? toJsDate(booking.dateDelivered) : undefined,
    DATE_OF_SALE: booking.dateOfSale ? toJsDate(booking.dateOfSale) : undefined,
    ESTIMATED_CHOICE_PERCENT: booking.estimatedChoicePercent || undefined,
    ESTIMATED_WEIGHT_NOTE: booking.estimatedWeightNote || "",
    FREIGHT_COST: booking.freightCost || "",
    GRID: booking.grid?.id || undefined,
    LOT_NUMBER: booking.lotNumber || "",
    PEN_NUMBER: booking.penNumber || "",
    PRICE: booking.price || "",
    PROGRAM: booking.program?.id || undefined,
    PURCHASE_PRICE_PER_CWT: booking.purchasePricePerCwt || "",
    RISK_MANAGEMENT: booking.riskManagement || false,
    SERVICE_PROVIDED: booking.serviceProvided || "",
    SEX_CLASS: booking.sexClass || undefined,
    SHIPPED_WEIGHT: booking.shippedWeight || undefined,
    SHRINK_PERCENT: booking.shrinkPercent || undefined,
    SLIDE: booking.slide?.toString() || "",
    SOURCE_NOTE: booking.sourceNote || "",
    TRUCKING_COMPANY: booking.truckingCompany?.id || undefined,
    VETERINARIAN: booking.veterinarian?.user?.id || undefined,
    BASE_WEIGHT: booking.baseWeight || undefined,
    LABELS: booking.labels?.map((label) => label.id) || [],
    DATE_OF_VISIT: booking.dateOfVisit ? toJsDate(booking.dateOfVisit) : undefined,
    CONDITION: booking.condition || undefined,
    CONDITIONS: removeNothings(booking?.conditions || []),
    OWNERSHIP_TYPE: nullable(booking.ownershipType) || undefined,
    PROJECTED_OUT_DATE: booking.projectedOutDate ? toJsDate(booking.projectedOutDate) : undefined,
  };
};

export const supplyChainBooking = graphql(`
  query GetSupplyChainBooking($id: UUID!) {
    supplyChainBooking(id: $id) {
      id
      name
      status
      numberOfHead
      plannedShippingDate
      note
      accountManager {
        user {
          id
          firstName
          lastName
        }
      }
      source {
        id
        supplyChainPartner {
          id
          businessName
        }
        address {
          id
          line1
          line2
          city
          state
          postcode
        }
      }
      destination {
        id
        supplyChainPartner {
          id
          businessName
        }
        address {
          id
          line1
          line2
          city
          state
          postcode
        }
      }
      sexClass
      confirmed
      lotNumber
      accreditations {
        id
        status
        label
      }
      price
      penNumber
      sourceNote
      program {
        id
        label
      }
      grid {
        id
        label
      }
      estimatedWeightNote
      shippedWeight
      veterinarian {
        id
        user {
          id
          firstName
          lastName
        }
      }
      brandNote
      serviceProvided
      certificateNumber
      freightCost
      dateOfSale
      broker {
        id
        businessName
      }
      purchasePricePerCwt
      slide
      shrinkPercent
      breedNote
      dateDelivered
      accountNumber
      riskManagement
      estimatedChoicePercent
      baseWeight
      labels {
        id
        status
        label
      }
      dateOfVisit
      truckingCompany {
        id
        businessName
      }
      condition
      conditions
      ownershipType
      projectedOutDate
      createdAt
    }
  }
`);

export const updateSupplyChainBookingMutation = graphql(`
  mutation UpdateSupplyChainBooking($input: UpdateSupplyChainBookingInput!) {
    updateSupplyChainBooking(input: $input) {
      supplyChainBooking {
        id
        name
        plannedShippingDate
        note
        numberOfHead
        status
        destination {
          id
        }
        source {
          id
        }
        accountManager {
          user {
            id
            firstName
            lastName
          }
        }
        sexClass
        confirmed
        lotNumber
        accreditations {
          id
          status
          label
        }
        price
        penNumber
        sourceNote
        program {
          id
          label
        }
        grid {
          id
          label
        }
        estimatedWeightNote
        shippedWeight
        veterinarian {
          id
          user {
            id
            firstName
            lastName
          }
        }
        brandNote
        serviceProvided
        certificateNumber
        freightCost
        dateOfSale
        broker {
          id
          businessName
        }
        purchasePricePerCwt
        slide
        shrinkPercent
        breedNote
        dateDelivered
        accountNumber
        riskManagement
        estimatedChoicePercent
        truckingCompany {
          id
          businessName
        }
        baseWeight
        labels {
          id
          status
          label
        }
        dateOfVisit
        condition
        conditions
        ownershipType
        projectedOutDate
      }
    }
  }
`);
